// Componente ResetPassword para actualización de contraseña
// Permite al usuario establecer una nueva contraseña después del proceso de recuperación

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import LayoutSignUp from '../../components/UI/LayoutSignUp';
import AmarsiLogo from '../../components/Icons/AmarsiLogo';
import Button from '../../components/UI/Button';
import PasswordInput from '../../components/UI/PasswordInput';
import toast from 'react-hot-toast';
import { updateUserPassword } from '../../services/authService';

/**
 * Componente ResetPassword
 * 
 * Formulario para actualizar la contraseña después de recibir el email de recuperación
 * Se activa cuando el usuario hace click en el link del email (evento PASSWORD_RECOVERY)
 */
const ResetPassword = () => {
  // Estado del formulario
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  
  // Estados para validación y UI
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);

  // Hooks
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Efecto para verificar si estamos en modo de recuperación
  useEffect(() => {
    const verifyRecoveryToken = async () => {
      // Verificar si hay parámetros de recuperación en la URL (query params)
      const params = new URLSearchParams(location.search);
      const tokenHash = params.get('token_hash');
      const type = params.get('type');

      // Verificar si hay parámetros en el hash (formato antiguo)
      const hashParams = new URLSearchParams(location.hash.substring(1));
      const hasAccessToken = hashParams.has('access_token') || hashParams.has('refresh_token');
      const isRecoveryType = hashParams.get('type') === 'recovery';

      // Si hay token_hash y type=recovery en query params, verificar el token
      if (tokenHash && type === 'recovery') {
        try {
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });

          if (error) {
            console.error('Error verificando token:', error);
            toast.error('El link de recuperación es inválido o ha expirado');
            return;
          }

          // Token verificado correctamente, activar modo de recuperación
          setIsRecoveryMode(true);
        } catch (err) {
          console.error('Error verificando token:', err);
          toast.error('Error al verificar el link de recuperación');
        }
      } 
      // Si hay parámetros en el hash (formato antiguo)
      else if (hasAccessToken || isRecoveryType) {
        setIsRecoveryMode(true);
      }
    };

    verifyRecoveryToken();
  }, [location]);

  // Maneja cambios en los campos del formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Limpia errores del campo cuando el usuario empieza a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Valida el formulario antes de enviar
  const validateForm = () => {
    const newErrors = {};

    // Validación de contraseña
    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    // Validación de confirmación
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Debes confirmar tu contraseña';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Maneja el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Verificar que el usuario esté autenticado (modo recuperación)
      if (!user || !session) {
        throw new Error('No hay una sesión activa. Por favor, usa el link del email de recuperación.');
      }

      // Actualizar la contraseña usando el servicio
      const result = await updateUserPassword(formData.password);

      if (result.success) {
        toast.success('Contraseña actualizada correctamente');
        
        // Redirigir al login después de un breve delay
        setTimeout(() => {
          navigate('/auth/login', { replace: true });
        }, 2000);
      } else {
        throw new Error('Error al actualizar la contraseña');
      }
    } catch (err) {
      console.error('Error actualizando contraseña:', err);
      toast.error(err.message || 'Error al actualizar la contraseña');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LayoutSignUp withProgressBar={false}>
      <div className="p-6">
        <div className="text-center mb-6">
          <AmarsiLogo width="80" height="80" className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">
            Restablecer contraseña
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mt-2">
            {isRecoveryMode 
              ? 'Ingresá tu nueva contraseña para continuar.'
              : 'Por favor, usa el link del email de recuperación para restablecer tu contraseña.'
            }
          </p>
        </div>

        {isRecoveryMode ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Campo Nueva Contraseña */}
            <PasswordInput
              label="Nueva contraseña"
              placeholder="Tu nueva contraseña"
              value={formData.password}
              onChange={(e) => handleChange({ target: { name: 'password', value: e.target.value } })}
              error={errors.password}
              autoFocus
              disabled={isSubmitting}
            />

            {/* Campo Confirmar Contraseña */}
            <PasswordInput
              label="Confirmar contraseña"
              placeholder="Confirmá tu nueva contraseña"
              value={formData.confirmPassword}
              onChange={(e) => handleChange({ target: { name: 'confirmPassword', value: e.target.value } })}
              error={errors.confirmPassword}
              disabled={isSubmitting}
            />

            {/* Botón de envío */}
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={!formData.password || !formData.confirmPassword || isSubmitting}
              size="md"
              className="w-full"
              variant="primary"
            >
              Actualizar contraseña
            </Button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Esta página solo es accesible a través del link de recuperación enviado a tu email.
              </p>
            </div>

            <Button
              onClick={() => navigate('/auth/forgot-password')}
              size="md"
              className="w-full"
              variant="secondary"
            >
              Solicitar nuevo email de recuperación
            </Button>
          </div>
        )}

        {/* Botón para volver al login */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/auth/login')}
            className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-500 font-medium"
          >
            ← Volver al inicio de sesión
          </button>
        </div>
      </div>
    </LayoutSignUp>
  );
};

export default ResetPassword;
